import { useEffect, type ReactNode } from "react";
import "./index.css";
import { documentMetaForPath, isInviteCodePath, isUniversalLinkAppPath, normalizePathname } from "./documentMeta";
import {
  Home,
  InviteCode,
  ExportWallet,
  PlaidOAuth,
  Support,
  ReturnToApp,
  AuthInviteCode,
  NotFound404,
} from "./pages";
import Download from "./pages/Download";

const routes: Record<string, () => ReactNode> = {
  "/": () => <Home />,
  "/export-wallet": () => <ExportWallet />,
  "/download": () => <Download />,
  "/support": () => <Support />,
};

const appRoutes: Record<string, () => ReactNode> = {
  "/plaid-oauth": () => <PlaidOAuth />,
  "/return-to-app": () => <ReturnToApp />,
  "/auth/invite-code": () => <AuthInviteCode />,
};

const AppHomeFallback = () => {
  window.location.replace("/");
  return null;
};

const UniversalLinksRouter = ({ pathname }: { pathname: string }) => {
  const appPath = pathname.slice("/app".length) || "/";
  if (appPath === "/") return <AppHomeFallback />;

  const Page = appRoutes[appPath];
  return Page ? <Page /> : <ReturnToApp />;
};

export const App = () => {
  // Normalized so a trailing slash resolves to the same route the worker
  // matched server-side.
  const pathname = normalizePathname(window.location.pathname);

  useEffect(() => {
    // Invite pages manage their own title so the worker's personalized
    // "{name} invited you to Goldsand" isn't clobbered with generic copy.
    if (isInviteCodePath(pathname)) return;
    const { title, description } = documentMetaForPath(pathname);
    document.title = title;
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);
    document.querySelector('meta[property="og:description"]')?.setAttribute("content", description);
    document.querySelector('meta[name="twitter:description"]')?.setAttribute("content", description);
  }, [pathname]);

  if (isUniversalLinkAppPath(pathname)) {
    return <UniversalLinksRouter pathname={pathname} />;
  }

  if (isInviteCodePath(pathname)) {
    return <InviteCode />;
  }

  const Page = routes[pathname];
  return Page ? <Page /> : <NotFound404 />;
};

export default App;
